import APopover from '../../../../components/APopover/APopover';
import messages from './Messages';
import React from 'react';
import HoverIcon from '../../../../components/HoverIcon/HoverIcon';
// import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export interface ExcelDataProps {
  [key: string]: any;
}

interface Props {
  getData: () => ExcelDataProps[];
  exportFileName: string;
  hasResult?: boolean;
  categorizedColumnInformation?: {
    columnNumber: number;
    columnTitle: string;
    otherToMergeColumnTitles: string[]; //todo: it's buggy and only accepts 1 param
  };
  width?: { [key: number | string]: number };
  body?: React.ReactNode;
}

function setColumnWidth(
  sheet: any,
  width: { [key: number | string]: number } | undefined
) {
  sheet.columns.forEach((column: any, index: number) => {
    const inConfig =
      width?.[index] || width?.[column._header] || width?.['default'];
    if (inConfig) {
      column.width = inConfig;
    } else {
      let maxLength = 0;
      column.eachCell?.({ includeEmpty: true }, (cell: any) => {
        const cellLength = cell.value ? String(cell.value).length : 0;
        maxLength = Math.max(maxLength, cellLength);
      });
      column.width = Math.min(120, maxLength + 2); // Add a bit of padding for readability
    }
  });
}

const ExportExcell: React.FC<Props> = ({
  getData,
  exportFileName,
  hasResult = false,
  categorizedColumnInformation,
  body,
  width,
}) => {
  const handleExportExcel = async () => {
    try {
      const data = getData();
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });
      const headers = Object.keys(data[0]);
      worksheet.columns = headers.map((item) => ({
        header: data[0][item],
        key: item,
        width: Math.max(...headers.map((header) => header.length + 2)),
      }));

      // for header style
      worksheet.getRow(1).font = {
        bold: true,
        color: { argb: 'FFFFFF' },
      };
      headers.forEach((_, index) => {
        const cell = worksheet.getCell(1, index + 1);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '000000' },
        };
      });
      if (categorizedColumnInformation) {
        const { columnTitle, columnNumber, otherToMergeColumnTitles } =
          categorizedColumnInformation;
        const groupedData = data?.slice(1)?.reduce((acc, item) => {
          if (!acc[item?.[columnTitle]]) {
            acc[item?.[columnTitle]] = [];
          }
          acc[item?.[columnTitle]].push(item);
          return acc;
        }, {});
        let rowIndex = 2;
        for (const group in groupedData) {
          const items = groupedData[group];
          items.forEach((item: any, index: number) => {
            const rowData = headers.map((header) => item[header]);
            worksheet.addRow(rowData);
            const currentRow = worksheet.getRow(rowIndex + index);
            currentRow.eachCell((cell) => {
              cell.alignment = {
                horizontal: 'right',
                readingOrder: 'rtl',
                wrapText: true,
              };
              if (item.isTotal) {
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'EEEEEE' },
                };
                cell.border = {
                  bottom: { style: 'thin', color: { argb: '444444' } }, // Blue bottom border
                };
              }
              if (item.isDisabled) {
                cell.font = {
                  color: { argb: '777777' },
                };
              }
            });
          });
          const mergedCell = worksheet.getCell(rowIndex, columnNumber);
          mergedCell.value = group;
          mergedCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'EEEEEE' },
          };
          mergedCell.border = {
            bottom: { style: 'thin', color: { argb: '444444' } }, // Blue bottom border
          };
          mergedCell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true,
          };
          worksheet.mergeCells(
            rowIndex,
            columnNumber,
            rowIndex + items.length - 1,
            columnNumber
          );
          for (const otherToMergeColumnTitle of otherToMergeColumnTitles) {
            const columnNumber = headers.indexOf(otherToMergeColumnTitle) + 1;
            const mergedCellEx = worksheet.getCell(rowIndex, columnNumber);
            mergedCellEx.value = items[0][otherToMergeColumnTitle];
            mergedCellEx.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'EEEEEE' },
            };
            mergedCellEx.border = {
              bottom: { style: 'thin', color: { argb: '444444' } }, // Blue bottom border
            };
            mergedCellEx.alignment = {
              horizontal: 'center',
              vertical: 'middle',
              wrapText: true,
            };
            worksheet.mergeCells(
              rowIndex,
              columnNumber,
              rowIndex + items.length - 1,
              columnNumber
            );

            rowIndex += items.length;
          }
        }
        if (hasResult) {
          const resultRow = worksheet.getRow(rowIndex - 1);
          resultRow.eachCell((cell) => {
            cell.font = {
              bold: true,
            };
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'EEEEEE' },
            };
          });
        }
      } else {
        data.slice(1).forEach((rowData, index) => {
          const row = worksheet.addRow(
            headers.map((header) => rowData[header])
          );
          row.eachCell((cell) => {
            cell.alignment = {
              horizontal: 'right',
              readingOrder: 'rtl',
              wrapText: true,
            };
          });
          row.getCell(1).font = {
            bold: true,
          };

          if (hasResult && index === data.slice(1).length - 1) {
            row.eachCell((cell) => {
              cell.font = {
                bold: true,
              };
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'EEEEEE' },
              };
            });
          }
        });
      }
      setColumnWidth(worksheet, width);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, `${exportFileName}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error); // Error handling added
    }
  };

  return (
    <APopover
      target={
        <div onClick={handleExportExcel}>
          {body || <HoverIcon type="download" color={'green'} />}
        </div>
      }
      arrow
      curveBorder={true}
      trigger="hover"
    >
      <div>{messages.ExportExcell}</div>
    </APopover>
  );
};

export default ExportExcell;
